import React, { useState } from 'react';

const SUPPORT_EMAIL = 'j.thondike@rewardsinwhatif.app';
const LAST_UPDATED = 'October 24, 2026';
const APP_NAME = 'WHATIF Battle For Riches';
const PACKAGE_NAME = 'app.rewardsinwhatif.game';
const WEBSITE_URL = 'https://rewardsinwhatif.app';

function PrivacyPage() {
  const [walletId, setWalletId] = useState('');
  const [sent, setSent] = useState(false);

  const requestDeletion = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Data Deletion Request – ${APP_NAME}`);
    const body = encodeURIComponent(
      `Hello WHATIF Rewards Support Team,\n\n` +
      `I am requesting the permanent deletion of all off-chain personal and gameplay data associated with my account.\n\n` +
      `App: ${APP_NAME} (${PACKAGE_NAME})\n` +
      `WAX Wallet / Account ID: ${walletId || '[please specify your wallet address]'}\n\n` +
      `I understand that this will permanently remove my in-game profile, off-chain progress, and database records from Firebase, and that transactions already recorded on the public WAX blockchain cannot be modified or erased.\n\n` +
      `Thank you.`
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  const sectionHead = {
    color: '#00e5ff',
    marginTop: '32px',
    marginBottom: '12px',
    borderBottom: '1px solid #333',
    paddingBottom: '8px',
    fontSize: '1.4em'
  };

  const linkStyle = {
    color: '#00e5ff',
    textDecoration: 'underline'
  };

  const subHead = {
    color: '#ff9800',
    marginTop: '16px',
    marginBottom: '6px',
    fontSize: '1.1em'
  };

  const textStyle = {
    color: '#d4d4d4',
    lineHeight: '1.7',
    margin: '10px 0'
  };

  return (
    <div style={{
      backgroundColor: 'rgba(15, 17, 23, 0.95)',
      padding: '36px',
      borderRadius: '12px',
      maxWidth: '920px',
      margin: '30px auto',
      textAlign: 'left',
      lineHeight: '1.7',
      border: '1px solid #333',
      color: '#e0e0e0',
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)'
    }}>
      <h1 style={{ textAlign: 'center', color: '#ff9800', fontSize: '2.4em', marginBottom: '4px' }}>
        Privacy Policy
      </h1>
      <h2 style={{ textAlign: 'center', color: '#00e5ff', fontSize: '1.3em', marginTop: '0', marginBottom: '8px', fontWeight: '500' }}>
        {APP_NAME} &amp; WHATIF Rewards Ecosystem
      </h2>
      <p style={{ textAlign: 'center', fontStyle: 'italic', color: '#888', marginTop: '0', fontSize: '0.95em' }}>
        Last updated: {LAST_UPDATED}
      </p>

      <hr style={{ borderColor: '#333', margin: '24px 0' }} />

      <p style={textStyle}>
        This Privacy Policy explains how <strong>WHATIF Rewards INC</strong> ("we", "us", or "our") collects, uses,
        discloses, and protects your information when you use our mobile application <strong>{APP_NAME}</strong> (Google Play
        Package: <code style={{ color: '#00e5ff', background: 'rgba(0,229,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>{PACKAGE_NAME}</code>),
        our web platform at <a href={WEBSITE_URL} style={linkStyle} target="_blank" rel="noopener noreferrer">rewardsinwhatif.app</a>,
        and associated services across the WHATIF ecosystem (collectively, the "Services").
      </p>
      <p style={textStyle}>
        We are committed to respecting user privacy and ensuring full transparency regarding data collection, device permissions,
        and third-party integrations. If you have questions regarding this policy or your data, please contact our support team at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={linkStyle}>{SUPPORT_EMAIL}</a>.
      </p>

      {/* ── 1. Information We Collect ── */}
      <h2 style={sectionHead}>1. Information We Collect and Process</h2>
      <p style={textStyle}>
        We only collect information necessary to operate the game, maintain security, calculate rewards, and deliver requested features:
      </p>
      
      <h3 style={subHead}>A. Blockchain Account &amp; Identity Data</h3>
      <p style={textStyle}>
        When you connect a WAX blockchain wallet (such as WAX Cloud Wallet or Anchor) to the Services, we record your public
        WAX account name (e.g. <code>user.wam</code>) as your primary player identifier. An internal, anonymized Firebase UID
        is generated upon sign-in to securely manage your authenticated session.
      </p>

      <h3 style={subHead}>B. Gameplay &amp; Progression Data</h3>
      <p style={textStyle}>
        We store in-game activity and progression records, including dungeon crawler runs, floor depths achieved, battle
        statistics, in-game gold earned, high scores, leaderboard rankings, staking balances, gang memberships, item unboxings,
        and forge history.
      </p>

      <h3 style={subHead}>C. Technical &amp; Device Information</h3>
      <p style={textStyle}>
        Our cloud infrastructure (Google Firebase) automatically logs standard technical diagnostic data, including Internet
        Protocol (IP) addresses, device hardware model, operating system version, app version, network request timestamps, and
        crash diagnostics. This data is used solely to maintain stability, prevent cheating and automated abuse, and troubleshoot
        technical issues.
      </p>

      <h3 style={subHead}>D. Advertising &amp; Device Identifiers (Android App)</h3>
      <p style={textStyle}>
        The Android version of <strong>{APP_NAME}</strong> integrates the <strong>Google Mobile Ads (AdMob)</strong> SDK to offer
        optional rewarded video ads (such as the Torch of Haste cooldown reducer, Campfire healing, 2x Dungeon Doubler, Dragon Shrine
        buffs, and Wheel of Riches bonus spins).
      </p>
      <p style={textStyle}>
        To serve, measure, and verify rewarded advertisements and prevent fraudulent interactions, Google AdMob may collect and process:
      </p>
      <ul style={{ color: '#ccc', paddingLeft: '24px', lineHeight: '1.8' }}>
        <li>Google Advertising ID (Android AD_ID / AAID)</li>
        <li>Device identifiers (device type, screen resolution, operating system version)</li>
        <li>IP address and general coarse location signals derived from IP</li>
        <li>Ad interaction and view completion verification events</li>
      </ul>
      <p style={textStyle}>
        For more information on how Google collects and processes advertising data, please review the{' '}
        <a href="https://policies.google.com/privacy" style={linkStyle} target="_blank" rel="noopener noreferrer">
          Google Privacy Policy
        </a>{' '}
        and Google's{' '}
        <a href="https://policies.google.com/technologies/ads" style={linkStyle} target="_blank" rel="noopener noreferrer">
          Advertising Terms
        </a>.
      </p>

      {/* ── 2. Information We Never Collect ── */}
      <h2 style={sectionHead}>2. Information We Never Collect</h2>
      <ul style={{ color: '#ccc', paddingLeft: '24px', lineHeight: '1.8' }}>
        <li>
          <strong>Private Keys &amp; Seed Phrases:</strong> We NEVER request, receive, or store your private cryptographic keys,
          wallet seed phrases, or wallet passwords. Transactions are signed client-side through your chosen wallet provider.
          We can never access your private keys or initiate unauthorized transfers.
        </li>
        <li>
          <strong>Sensitive Personal Information:</strong> We do not ask for or collect your real legal name, physical address,
          phone number, government ID, or credit/debit card numbers.
        </li>
        <li>
          <strong>Data Brokerage:</strong> We do NOT sell, rent, or trade your personal or gameplay data to data brokers or third parties.
        </li>
      </ul>

      {/* ── 3. How We Use Your Data ── */}
      <h2 style={sectionHead}>3. How We Use Your Data</h2>
      <ul style={{ color: '#ccc', paddingLeft: '24px', lineHeight: '1.8' }}>
        <li>To create, manage, and maintain your player profile and dungeon progression.</li>
        <li>To calculate leaderboard rankings, season standings, and token reward distributions.</li>
        <li>To deliver and verify optional rewarded video advertisements via Google AdMob.</li>
        <li>To monitor server health, detect security breaches, and prevent automated bots or cheating.</li>
        <li>To respond to user inquiries and process data deletion requests.</li>
      </ul>

      {/* ── 4. Third-Party Service Providers ── */}
      <h2 style={sectionHead}>4. Third-Party Service Providers &amp; Data Sharing</h2>
      <p style={textStyle}>
        We share information only with trusted third-party service providers that assist in delivering the Services, under strict
        confidentiality and security standards:
      </p>
      <ul style={{ color: '#ccc', paddingLeft: '24px', lineHeight: '1.8' }}>
        <li>
          <strong>Google Firebase (Google LLC):</strong> Cloud hosting, serverless backend functions, real-time Firestore database,
          and Firebase App Check security verification.
        </li>
        <li>
          <strong>Google AdMob (Google LLC):</strong> Serves optional rewarded video advertisements and provides ad-performance analytics.
        </li>
        <li>
          <strong>WAX Blockchain (Decentralized Ledger):</strong> Public smart contracts and node infrastructure used to record
          token reward claims and NFT interactions.
        </li>
        <li>
          <strong>AtomicHub / IPFS:</strong> Decentralized decentralized storage and API gateways used to display NFT artwork and metadata.
        </li>
      </ul>

      {/* ── 5. Public Blockchain Disclosures ── */}
      <h2 style={sectionHead}>5. Public Blockchain Notice</h2>
      <p style={textStyle}>
        The WAX Blockchain is a public, decentralized, immutable distributed ledger. Any transaction submitted to the WAX blockchain
        (including token swaps, staking, claims, and NFT transfers) is permanently recorded and publicly accessible to anyone worldwide.
        Because public blockchain transactions cannot be edited, modified, or deleted by any single party, information written to the
        WAX blockchain is outside the scope of deletion requests.
      </p>

      {/* ── 6. Children's Privacy ── */}
      <h2 style={sectionHead}>6. Children's Privacy</h2>
      <p style={textStyle}>
        Our Services are not designed for or directed to children under the age of 13 (or under 16 in certain jurisdictions, such as
        the European Economic Area and UK). We do not knowingly collect or solicit personal information from children. If we discover
        that a child has provided us with personal information, we will take immediate steps to delete such data from our servers.
        If you believe a child has provided us with personal information, please notify us immediately at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={linkStyle}>{SUPPORT_EMAIL}</a>.
      </p>

      {/* ── 7. Data Retention & Deletion Rights ── */}
      <h2 style={sectionHead}>7. Data Retention &amp; User Deletion Rights</h2>
      <p style={textStyle}>
        You have the right to request access to or permanent deletion of all off-chain personal data and game records stored in our
        Firebase databases. Upon receiving a verified request, we will permanently delete your off-chain profile and associated
        records within <strong>30 days</strong>.
      </p>

      <div style={{
        background: 'rgba(255, 152, 0, 0.08)',
        border: '1px solid rgba(255, 152, 0, 0.4)',
        padding: '24px',
        borderRadius: '8px',
        marginTop: '20px'
      }}>
        <h3 style={{ color: '#ff9800', marginTop: 0, marginBottom: '10px' }}>Request Account Data Deletion</h3>
        <p style={{ ...textStyle, fontSize: '0.95em', margin: '0 0 16px 0' }}>
          Enter your WAX wallet address below to generate an official data deletion request to our support team:
        </p>

        <form onSubmit={requestDeletion}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <input
              id="wallet-delete-input"
              type="text"
              value={walletId}
              onChange={(e) => setWalletId(e.target.value)}
              placeholder="e.g. yourwallet.wam"
              style={{
                padding: '10px 14px',
                flex: '1',
                minWidth: '240px',
                maxWidth: '380px',
                background: '#111',
                color: '#fff',
                border: '1px solid #555',
                borderRadius: '6px',
                fontSize: '1em'
              }}
            />
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                background: '#ff9800',
                color: '#111',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1em'
              }}
            >
              Submit Deletion Request
            </button>
          </div>
          {sent && (
            <p style={{ color: '#8bc34a', marginTop: '12px', fontSize: '0.95em' }}>
              ✓ Your email client has been launched with a pre-filled deletion request. If it did not open, please send an email directly to{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} style={linkStyle}>{SUPPORT_EMAIL}</a> with your wallet address.
            </p>
          )}
        </form>
      </div>

      {/* ── 8. Security Measures ── */}
      <h2 style={sectionHead}>8. Security Measures</h2>
      <p style={textStyle}>
        We implement administrative, technical, and physical safeguards to protect your information against unauthorized access, loss,
        or alteration. All network communication between the app and our backend is encrypted using standard HTTPS/TLS protocols.
        Database access is governed by server-side Firebase Security Rules enforcing least-privilege principles.
      </p>

      {/* ── 9. Policy Updates ── */}
      <h2 style={sectionHead}>9. Changes to This Privacy Policy</h2>
      <p style={textStyle}>
        We may update this Privacy Policy from time to time to reflect changes in our practices or applicable legal requirements.
        When updates occur, the "Last updated" date at the top of this page will be revised. We encourage you to review this policy
        periodically.
      </p>

      {/* ── 10. Contact Us ── */}
      <h2 style={sectionHead}>10. Contact Us</h2>
      <p style={textStyle}>
        If you have any questions, concerns, or requests regarding this Privacy Policy or our data handling practices, please contact:
      </p>
      <p style={{ ...textStyle, marginLeft: '12px' }}>
        <strong>WHATIF Rewards INC</strong><br />
        Support Email: <a href={`mailto:${SUPPORT_EMAIL}`} style={linkStyle}>{SUPPORT_EMAIL}</a><br />
        Website: <a href={WEBSITE_URL} style={linkStyle} target="_blank" rel="noopener noreferrer">{WEBSITE_URL}</a>
      </p>
    </div>
  );
}

export default PrivacyPage;
